<template>
  <div>
    <a-modal title="业务需求报价评估" :visible="visible" :width="500" @cancel="onCancel" @ok="onOk">
      <template slot="footer">
        <a-button @click="onCancel">取消</a-button>
        <a-button type="primary" :loading="saveLoading" @click="onOk">保存</a-button>
      </template>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <a-spin size="large">
          <div class="loading-text">正在加载评估任务...</div>
        </a-spin>
      </div>

      <!-- 评估任务列表 -->
      <div v-else-if="assessTaskList.length > 0" class="assess-task-list">
        <div class="task-header">
          <h4>团队报价评估</h4>
          <p class="task-desc">请为每个团队设置需求报价(1BRP ≈ 2TP)</p>
        </div>

        <div class="task-items">
          <div v-for="(task, index) in assessTaskList" :key="index" class="task-item">
            <div class="team-info">
              <a-icon type="team" class="team-icon" />
              <span class="team-name">{{ task.teamName }}</span>
            </div>
            <div class="point-input">
              <a-input-number v-model="task.brPoint" :min="0" :max="maxPoint" :precision="0" placeholder="请输入报价" style="width: 120px" />
              <span class="point-unit">BRP</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="empty-state">
        <a-empty description="暂无评估任务">
          <span slot="description"> 暂时还没有需要您评估的任务哦~ </span>
        </a-empty>
      </div>
    </a-modal>
    <a-button type="primary" @click="onOpen">报价评估</a-button>
  </div>
</template>

<script>
import { assessTaskQuery, assessTaskSave } from '@/api/business/assess';

export default {
  name: 'BrTeamAssessModal',
  props: {
    brId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      maxPoint: 1000,
      visible: false,
      loading: false,
      saveLoading: false,
      assessTaskList: []
    };
  },
  // 组件销毁时清理数据
  beforeDestroy() {
    this.resetData();
  },
  methods: {
    /**
     * 打开弹窗并加载数据
     */
    onOpen() {
      this.visible = true;
      this.getAssessTaskList();
    },

    /**
     * 获取评估任务列表
     */
    getAssessTaskList() {
      this.loading = true;
      assessTaskQuery({
        brId: this.brId,
        about: 'owner'
      })
        .then(res => {
          this.assessTaskList = res.data.items.map(item => ({
            ...item,
            brPoint: item.brPoint || undefined
          }));
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * 取消操作
     */
    onCancel() {
      this.visible = false;
      this.resetData();
    },

    /**
     * 保存评估任务
     */
    onOk() {
      // 表单验证
      const validationResult = this.validateForm();
      if (!validationResult.isValid) {
        this.$message.warning(validationResult.message);
        return;
      }

      this.saveLoading = true;

      const params = this.assessTaskList.map(item => ({
        teamId: item.teamId,
        point: item.brPoint
      }));

      assessTaskSave({
        brId: this.brId,
        params: params
      })
        .then(() => {
          this.$message.success('保存成功');
          this.visible = false;
          this.resetData();
          this.$emit('refresh');
        })
        .catch(error => {
          console.error('保存失败:', error);
          this.$message.error(error.message || '保存失败，请重试');
        })
        .finally(() => {
          this.saveLoading = false;
        });
    },

    /**
     * 表单验证
     */
    validateForm() {
      // 检查是否有评估任务
      if (!this.assessTaskList || this.assessTaskList.length === 0) {
        return {
          isValid: false,
          message: '暂无评估任务'
        };
      }

      // 检查是否所有团队都已填写点数
      const emptyTasks = this.assessTaskList.filter(task => task.brPoint === undefined || task.brPoint === null || task.brPoint === '');

      if (emptyTasks.length > 0) {
        const emptyTeamNames = emptyTasks.map(task => task.teamName).join('、');
        return {
          isValid: false,
          message: `请为 ${emptyTeamNames} 填写需求点数`
        };
      }

      return {
        isValid: true,
        message: ''
      };
    },

    /**
     * 重置数据
     */
    resetData() {
      this.assessTaskList = [];
      this.loading = false;
      this.saveLoading = false;
    }
  }
};
</script>

<style lang="less" scoped>
.loading-container {
  text-align: center;
  padding: 40px 20px;

  .loading-text {
    margin-top: 16px;
    color: #86909c;
    font-size: 14px;
  }
}

.assess-task-list {
  .task-header {
    margin-bottom: 20px;
    text-align: center;

    h4 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 600;
      color: #1d2129;
    }

    .task-desc {
      margin: 0;
      font-size: 13px;
      color: #86909c;
      line-height: 1.5;
    }
  }

  .task-items {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .task-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    background: #f7f8fa;
    border-radius: 8px;
    border: 1px solid #f2f3f5;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: #ffffff;
      border-color: #165dff;
      box-shadow: 0 2px 8px rgba(22, 93, 255, 0.1);
      transform: translateY(-1px);
    }

    .team-info {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;

      .team-icon {
        color: #165dff;
        font-size: 16px;
      }

      .team-name {
        font-size: 14px;
        font-weight: 500;
        color: #1d2129;
      }
    }

    .point-input {
      position: relative;
      display: flex;
      align-items: center;
      gap: 8px;

      .point-unit {
        font-size: 13px;
        color: #86909c;
        font-weight: 500;
      }
    }
  }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
}

// 自定义输入框样式
:deep(.ant-input-number) {
  border-radius: 6px;

  &:hover,
  &:focus {
    border-color: #165dff;
    box-shadow: 0 0 0 2px rgba(22, 93, 255, 0.1);
  }

  .ant-input-number-input {
    text-align: center;
    font-weight: 500;
  }
}

// 模态框内容区域样式调整
:deep(.ant-modal-body) {
  padding: 24px;
}

// Empty组件样式调整
:deep(.ant-empty) {
  .ant-empty-description {
    color: #86909c;
    font-size: 13px;
  }
}
</style>
